/* ═══════════════════════════════════════════════════════════════
   Level Properties panel — extracted from the classic hand-built forma
   canvas (CanvasComponents.tsx) when that canvas retired (contract §3
   R6, phase C: parity proven). Real, active consumer: the Designer/
   Studio's shared Levels palette (forma-studio/panels/FormaPalettes.tsx's
   LevelPalette, used by both the Designer's project-forma editing and
   the Master Forma Studio) — not classic-only, so it survives the
   retirement as its own small module.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { Check } from 'lucide-react';
import { CSI_NUMBER_FORMATS, FONTS } from '../constants';
import { effectiveCase } from '../utils';
import { Hint } from './ui/tooltip';
import { FORMA_TIPS } from '../formaTooltips';
import { ColorPickerPopover } from './ui/color-picker';
import { Icon } from './ui/Icon';
import { Switch } from './ui/switch';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { SearchableSelect as SearchableSelectKV } from './SearchableSelect';
import { rem } from '../lib/rem';

export type LevelGroupId =
  | 'character'
  | 'font'
  | 'alignment'
  | 'indents'
  | 'spacing'
  | 'pagination'
  | 'numbering';

/** The panel's own "Same as Typography" switch, as a pure transform — the
 *  panel and the level right-click menu both call it, so they cannot drift. */
export function levelFollowTypography(level: any, on: boolean, typoFont: string, typoSize: number) {
  if (!on) return { ...level, font: typoFont, fontSize: typoSize };
  // Drop the keys outright so nothing serializes as null.
  const next = { ...level };
  delete next.font;
  delete next.fontSize;
  return next;
}

/** Scroll a level-panel group into view and focus its first control. The
 *  panel may still be mounting (the dock tab was just opened), so this waits
 *  a few frames for the group to appear. No new data — it only moves focus. */
export function focusLevelGroup(group: LevelGroupId, tries = 30): void {
  const el = document.querySelector<HTMLElement>(`[data-level-group="${group}"]`);
  if (!el) {
    if (tries > 0) requestAnimationFrame(() => focusLevelGroup(group, tries - 1));
    return;
  }
  el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  el.querySelector<HTMLElement>('button, input, [role="switch"], [tabindex]')?.focus({
    preventScroll: true,
  });
}

export function LevelProps({
  level,
  levelIdx,
  onUpdate,
  numFormats,
  onNumFmtChange,
  bodyFont,
  bodyFontSize,
  formatBar,
}: {
  level: any;
  levelIdx: number;
  onUpdate: (l: any) => void;
  numFormats: string[];
  onNumFmtChange: (idx: number, fmt: string) => void;
  /** Typography values a level follows when it has no font of its own. */
  bodyFont?: string;
  bodyFontSize?: number;
  /** The Forma Studio's Format Bar owns the character, font, alignment and
   *  spacing controls (SYNC_PLAN §3.0) — hide those groups here. Omitted =
   *  every group, as before (the Spec Studio Designer has no bar). */
  formatBar?: boolean;
}) {
  const sameId = React.useId();

  const sizeId = React.useId();
  const sameAsTypography = !level.font && !level.fontSize;
  // Last-resort defaults mirror resolvePageGeometry (what the canvas draws
  // when Typography has no value); every real forma has both set.
  const typoFont = bodyFont || 'Times New Roman';
  const typoSize = bodyFontSize || 12;
  // Size is edited as text and committed on blur / Enter, clamped to 6-16.
  const [sizeDraft, setSizeDraft] = React.useState<string | null>(null);
  const commitSize = () => {
    if (sizeDraft === null) return;
    const n = Number(sizeDraft);
    const next =
      sizeDraft.trim() === '' || !Number.isFinite(n) ? typoSize : Math.min(16, Math.max(6, n));
    setSizeDraft(null);
    onUpdate({ ...level, fontSize: next });
  };
  const setSame = (on: boolean) => onUpdate(levelFollowTypography(level, on, typoFont, typoSize));
  const tog = (k: string) => onUpdate({ ...level, [k]: !level[k] });
  const setCase = (c: 'upper' | 'title' | 'sentence') => {
    const next = effectiveCase(level) === c ? undefined : c;
    onUpdate({ ...level, textCase: next, allCaps: next === 'upper' });
  };
  const charBtns = [
    { k: 'bold', l: 'B', s: { fontWeight: 700 }, tip: FORMA_TIPS.levelBold, aria: 'Bold' },
    {
      k: 'italic',
      l: 'I',
      s: { fontStyle: 'italic' },
      tip: FORMA_TIPS.levelItalic,
      aria: 'Italic',
    },
    {
      k: 'underline',
      l: 'U',
      s: { textDecoration: 'underline' },
      tip: FORMA_TIPS.levelUnderline,
      aria: 'Underline',
    },
  ];
  const caseBtns: Array<{ c: 'upper' | 'title' | 'sentence'; l: string; t: string }> = [
    { c: 'upper', l: 'AA', t: FORMA_TIPS.levelCaseUpper },
    { c: 'title', l: 'Aa', t: FORMA_TIPS.levelCaseTitle },
    { c: 'sentence', l: 'ab', t: FORMA_TIPS.levelCaseSentence },
  ];
  const pagBtns = [
    { k: 'keepWithNext', l: 'Keep w/ next', tip: FORMA_TIPS.levelKeepWithNext },
    { k: 'keepLinesTogether', l: 'Keep together', tip: FORMA_TIPS.levelKeepTogether },
    { k: 'noSpaceSameStyle', l: 'No space same', tip: FORMA_TIPS.levelNoSpaceSame },
  ];
  const LS: React.CSSProperties = {
    fontSize: 'var(--font-size-xs)',
    fontWeight: 700,
    color: 'var(--tm)',
    textTransform: 'uppercase',
    letterSpacing: '.05em',
  };
  const IS: React.CSSProperties = {
    width: rem(56),
    padding: `var(--space-1) ${rem(6)}`,
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--bd)',
    background: 'var(--ib)',
    color: 'var(--tx)',
    fontSize: 'var(--font-size-sm)',
    fontFamily: "'JetBrains Mono'",
  };

  const fmtInfo = (CSI_NUMBER_FORMATS as any)[levelIdx];
  const hasOptions = fmtInfo && fmtInfo.options.length > 1;
  const currentFmt = numFormats ? numFormats[levelIdx] : fmtInfo?.options[0]?.id;

  // sf-palette-flow: when this renders inside a wide/short dock panel its
  // sections tile into columns like every other palette (FormaDock.css).
  // Inert in the right-click popover, which is never .forma-panel-horizontal.
  return (
    <div
      className="sf-palette-flow"
      style={{
        padding: 'var(--space-3) var(--space-4)',
        background: 'var(--sf)',
        fontSize: 'calc(var(--font-size-base) * 12 / 13)',
        display: 'flex',
        flexDirection: 'column',
        gap: rem(14),
      }}
    >
      {!formatBar && (
        <>
          <div
            data-level-group="character"
            style={{ display: 'flex', flexDirection: 'column', gap: rem(5) }}
          >
            <span style={LS}>Character</span>
            <div style={{ display: 'flex', gap: rem(3) }}>
              {charBtns.map((b) => (
                <Hint key={b.k} text={b.tip}>
                  <button
                    aria-label={b.aria}
                    aria-pressed={!!level[b.k]}
                    onClick={() => tog(b.k)}
                    style={{
                      width: rem(26),
                      height: rem(26),
                      borderRadius: 'var(--radius-sm)',
                      border: `1px solid ${level[b.k] ? 'var(--ac)' : 'var(--bd)'}`,
                      background: level[b.k] ? 'var(--ac)' : 'transparent',
                      color: level[b.k] ? 'var(--color-text-inverse)' : 'var(--tx)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      ...b.s,
                      transition: 'all .15s',
                    }}
                  >
                    {b.l}
                  </button>
                </Hint>
              ))}
              <div
                style={{ width: rem(1), background: 'var(--bd)', margin: `${rem(2)} ${rem(1)}` }}
              />
              {caseBtns.map((b) => {
                const on = effectiveCase(level) === b.c;
                return (
                  <Hint key={b.c} text={b.t}>
                    <button
                      aria-label={b.t}
                      aria-pressed={on}
                      onClick={() => setCase(b.c)}
                      style={{
                        width: rem(28),
                        height: rem(26),
                        borderRadius: 'var(--radius-sm)',
                        border: `1px solid ${on ? 'var(--ac)' : 'var(--bd)'}`,
                        background: on ? 'var(--ac)' : 'transparent',
                        color: on ? 'var(--color-text-inverse)' : 'var(--tx)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 700,
                        transition: 'all .15s',
                      }}
                    >
                      {b.l}
                    </button>
                  </Hint>
                );
              })}
              <Hint text={FORMA_TIPS.levelColor}>
                <ColorPickerPopover
                  aria-label="Text color"
                  value={`#${level.fontColor || '000000'}`}
                  onChange={(hex) => onUpdate({ ...level, fontColor: hex.replace('#', '') })}
                  allowAlpha={false}
                  triggerClassName="ml-1 h-[26px] w-[26px]"
                />
              </Hint>
            </div>
          </div>
          <div
            data-level-group="font"
            style={{ display: 'flex', flexDirection: 'column', gap: rem(5) }}
          >
            <Hint text={FORMA_TIPS.levelFont}>
              <span tabIndex={0} style={LS}>
                Font
              </span>
            </Hint>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 'var(--space-2)',
              }}
            >
              <label
                htmlFor={sameId}
                style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tx)', cursor: 'pointer' }}
              >
                Same as Typography
              </label>
              <Switch id={sameId} checked={sameAsTypography} onCheckedChange={setSame} />
            </div>
            {sameAsTypography ? (
              <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)' }}>
                {typoFont} · {typoSize}pt
              </span>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: rem(6) }}>
                <div>
                  <label style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)' }}>
                    Font
                  </label>
                  <SearchableSelectKV
                    value={level.font ?? typoFont}
                    onChange={(v: string) => onUpdate({ ...level, font: v })}
                    options={FONTS.map((f) => ({ value: f, label: f }))}
                    clearable={false}
                    searchPlaceholder="Search fonts…"
                    ariaLabel="Level font"
                  />
                </div>
                <div>
                  <label
                    htmlFor={sizeId}
                    style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)' }}
                  >
                    Size (pt)
                  </label>
                  <Input
                    id={sizeId}
                    type="number"
                    min="6"
                    max="16"
                    value={sizeDraft ?? String(level.fontSize || typoSize)}
                    onChange={(e) => setSizeDraft(e.target.value)}
                    onBlur={commitSize}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') commitSize();
                    }}
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setSame(true)}
                  title="Drop this level's own font and size — it follows the Typography toolbox again"
                  style={{ alignSelf: 'flex-start' }}
                >
                  Reset to Typography
                </Button>
              </div>
            )}
          </div>
          <div
            data-level-group="alignment"
            style={{ display: 'flex', flexDirection: 'column', gap: rem(5) }}
          >
            <span style={LS}>Alignment</span>
            <div style={{ display: 'flex', gap: rem(3) }}>
              {(
                [
                  {
                    v: 'left',
                    t: FORMA_TIPS.levelAlignLeft,
                    a: 'Align left',
                    d: 'M2 3h10M2 6h7M2 9h10M2 12h7',
                  },
                  {
                    v: 'center',
                    t: FORMA_TIPS.levelAlignCenter,
                    a: 'Center',
                    d: 'M2 3h10M3.5 6h7M2 9h10M3.5 12h7',
                  },
                  {
                    v: 'right',
                    t: FORMA_TIPS.levelAlignRight,
                    a: 'Align right',
                    d: 'M2 3h10M5 6h7M2 9h10M5 12h7',
                  },
                  {
                    v: 'justify',
                    t: FORMA_TIPS.levelAlignJustify,
                    a: 'Justify',
                    d: 'M2 3h10M2 6h10M2 9h10M2 12h10',
                  },
                ] as Array<{ v: string; t: string; a: string; d: string }>
              ).map((b) => {
                const on = (level.justify || 'left') === b.v;
                return (
                  <Hint key={b.v} text={b.t}>
                    <button
                      aria-label={b.a}
                      aria-pressed={on}
                      onClick={() =>
                        onUpdate({ ...level, justify: b.v === 'left' ? undefined : b.v })
                      }
                      style={{
                        width: rem(26),
                        height: rem(26),
                        borderRadius: 'var(--radius-sm)',
                        border: `1px solid ${on ? 'var(--ac)' : 'var(--bd)'}`,
                        background: on ? 'var(--ac)' : 'transparent',
                        color: on ? 'var(--color-text-inverse)' : 'var(--tx)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all .15s',
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
                        <path
                          d={b.d}
                          stroke="currentColor"
                          strokeWidth="1.4"
                          strokeLinecap="round"
                        />
                      </svg>
                    </button>
                  </Hint>
                );
              })}
            </div>
          </div>
        </>
      )}
      <div
        data-level-group="indents"
        style={{ display: 'flex', flexDirection: 'column', gap: rem(5) }}
      >
        <span style={LS}>Indent (in)</span>
        <div style={{ display: 'flex', gap: rem(6) }}>
          <div>
            <Hint text={FORMA_TIPS.levelIndent}>
              <span tabIndex={0} style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)' }}>
                Left
              </span>
            </Hint>
            <br />
            <input
              type="number"
              aria-label="Left indent, inches"
              step="0.25"
              min="0"
              max="5"
              value={level.indent}
              onChange={(e) =>
                onUpdate({ ...level, indent: Math.max(0, Number(e.target.value) || 0) })
              }
              style={IS}
            />
          </div>
          <div>
            <Hint text={FORMA_TIPS.levelHanging}>
              <span tabIndex={0} style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)' }}>
                Hanging
              </span>
            </Hint>
            <br />
            <input
              type="number"
              aria-label="Hanging indent, inches"
              step="0.25"
              min={-(level.indent || 0)}
              max="3"
              value={level.hangingIndent}
              onChange={(e) =>
                onUpdate({
                  ...level,
                  hangingIndent: Math.max(-(level.indent || 0), Number(e.target.value) || 0),
                })
              }
              style={IS}
            />
          </div>
        </div>
      </div>
      {!formatBar && (
        <div
          data-level-group="spacing"
          style={{ display: 'flex', flexDirection: 'column', gap: rem(5) }}
        >
          <span style={LS}>Spacing (pt)</span>
          <div style={{ display: 'flex', gap: rem(6) }}>
            <div>
              <Hint text={FORMA_TIPS.levelSpaceBefore}>
                <span tabIndex={0} style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)' }}>
                  Before
                </span>
              </Hint>
              <br />
              <input
                type="number"
                aria-label="Space before, points"
                step="1"
                min="0"
                max="72"
                value={level.spaceBefore}
                onChange={(e) =>
                  onUpdate({ ...level, spaceBefore: Math.max(0, Number(e.target.value) || 0) })
                }
                style={{ ...IS, width: 'var(--space-7)' }}
              />
            </div>
            <div>
              <Hint text={FORMA_TIPS.levelSpaceAfter}>
                <span tabIndex={0} style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)' }}>
                  After
                </span>
              </Hint>
              <br />
              <input
                type="number"
                aria-label="Space after, points"
                step="1"
                min="0"
                max="72"
                value={level.spaceAfter}
                onChange={(e) =>
                  onUpdate({ ...level, spaceAfter: Math.max(0, Number(e.target.value) || 0) })
                }
                style={{ ...IS, width: 'var(--space-7)' }}
              />
            </div>
          </div>
        </div>
      )}
      <div
        data-level-group="pagination"
        style={{ display: 'flex', flexDirection: 'column', gap: rem(5) }}
      >
        <span style={LS}>Pagination</span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: rem(6) }}>
          {pagBtns.map((c) => (
            <Hint key={c.k} text={c.tip}>
              <label
                tabIndex={0}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-1)',
                  cursor: 'pointer',
                  fontSize: 'var(--font-size-sm)',
                }}
              >
                <div
                  role="checkbox"
                  aria-checked={!!level[c.k]}
                  aria-label={c.l}
                  onClick={() => tog(c.k)}
                  style={{
                    width: rem(15),
                    height: rem(15),
                    borderRadius: rem(3),
                    border: `1.5px solid ${level[c.k] ? 'var(--ac)' : 'var(--bd)'}`,
                    background: level[c.k] ? 'var(--ac)' : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--color-text-inverse)',
                    fontSize: 'var(--font-size-sm)',
                    cursor: 'pointer',
                    transition: 'all .15s',
                    flexShrink: 0,
                  }}
                >
                  {level[c.k] ? <Icon icon={Check} size="md" strokeWidth={3} aria-hidden /> : null}
                </div>
                {c.l}
              </label>
            </Hint>
          ))}
        </div>
      </div>
      {hasOptions && (
        <div
          data-level-group="numbering"
          style={{ display: 'flex', flexDirection: 'column', gap: rem(5) }}
        >
          <Hint text={FORMA_TIPS.levelNumbering}>
            <span tabIndex={0} style={LS}>
              Numbering
            </span>
          </Hint>
          <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
            {fmtInfo.options.map((o: any) => (
              <button
                key={o.id}
                onClick={() => onNumFmtChange(levelIdx, o.id)}
                style={{
                  padding: `${rem(3)} var(--space-2)`,
                  borderRadius: 'var(--radius-sm)',
                  border: `1px solid ${currentFmt === o.id ? 'var(--ac)' : 'var(--bd)'}`,
                  background: currentFmt === o.id ? 'rgba(var(--acr),0.12)' : 'transparent',
                  color: currentFmt === o.id ? 'var(--ac)' : 'var(--tm)',
                  fontWeight: currentFmt === o.id ? 700 : 400,
                  fontSize: 'var(--font-size-sm)',
                  cursor: 'pointer',
                  fontFamily: "'JetBrains Mono'",
                  transition: 'all .15s',
                }}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
